import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "currentPhoto", "previewContainer", "previewImage", "fileInfo", "errorContainer", "errorMessage"]
  static outlets = ["image-cropper"]

  connect() {
    this.maxFileSize = 10 * 1024 * 1024 // 10MB
    this.allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"]
  }

  async handleFileSelect(event) {
    const fileInput = event.target
    const file = fileInput.files[0]
    if (!file) return

    this.hideError()

    if (!this.validateFile(file)) return

    if (this.hasImageCropperOutlet) {
      const blob = await this.imageCropperOutlet.open(file)
      if (blob) {
        this.applyCroppedBlob(blob, file.name, fileInput)
      } else {
        // User cancelled — reset file input
        fileInput.value = ""
      }
    } else {
      this.showPreview(file)
    }
  }

  applyCroppedBlob(blob, originalName, fileInput) {
    const croppedName = originalName.replace(/\.[^.]+$/, "") + "_cropped.jpg"
    const croppedFile = new File([blob], croppedName, { type: "image/jpeg" })

    // Replace file input value via DataTransfer API
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(croppedFile)
    fileInput.files = dataTransfer.files

    // Show preview of cropped image
    const url = URL.createObjectURL(blob)
    this.previewImageTarget.src = url
    this.fileInfoTarget.textContent = `${croppedName} (${this.formatFileSize(blob.size)})`
    this.previewContainerTarget.classList.remove("hidden")

    if (this.hasCurrentPhotoTarget) {
      this.currentPhotoTarget.classList.add("hidden")
    }
  }

  validateFile(file) {
    if (!this.allowedTypes.includes(file.type)) {
      this.showError("Please select a JPEG, PNG, or WebP image")
      return false
    }

    if (file.size > this.maxFileSize) {
      this.showError("File size must be less than 10MB")
      return false
    }

    return true
  }

  showPreview(file) {
    const reader = new FileReader()

    reader.onload = (e) => {
      this.previewImageTarget.src = e.target.result
      this.fileInfoTarget.textContent = `${file.name} (${this.formatFileSize(file.size)})`
      this.previewContainerTarget.classList.remove("hidden")

      if (this.hasCurrentPhotoTarget) {
        this.currentPhotoTarget.classList.add("hidden")
      }
    }

    reader.readAsDataURL(file)
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorContainerTarget.classList.remove("hidden")
    this.previewContainerTarget.classList.add("hidden")
  }

  hideError() {
    this.errorContainerTarget.classList.add("hidden")
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
  }
}
